<template>
    <explore-widget
        widgetTitle="Enrollment"
        toolTip="Enrollment data is provided by your Student Information System (SIS) and syncs with eduCLIMBER each night"
        dataTestBaseString="enrollment"
        :cardHeight="launchpadWidgetHeights.thirdRow"
        @exploreClick="enrollmentClicked"
        @printChart="printChart"
        @exportChart="exportChart"
    >
        <template v-slot:widget-body>
            <div v-if="!ready">
                <fe-spinner class="spinner"/>
            </div>
            <div v-else-if="params" id="enrollment-data-viz" class="enr-card-body pt-3">
                <div class="d-flex enr-card-details flex-grow-1">
                    <div class="enr-total">
                        <div
                            class="enr-total-num"
                            tabindex="0"
                            role="button"
                            :aria-label="`View enrollment details for yesterday: ${totalEnrDisplay} students`"
                            data-test="enrollment-yesterday-num"
                            @click="enrollmentClicked"
                            @keydown.enter="enrollmentClicked"
                            @keydown.space.prevent="enrollmentClicked"
                            style="cursor: pointer;"
                        >{{ totalEnrDisplay }}</div>
                        <div class="enr-total-text">yesterday's enrollment</div>
                    </div>

                    <v-spacer/>

                    <div class="enr-changes">
                        <div v-if="fromLastYear.num != 0" class="d-flex changes-row">
                            <div
                                class="changes-value"
                                tabindex="0"
                                role="button"
                                :aria-label="`View enrollment change from last year: ${fromLastYear.delta} ${fromLastYear.num}`"
                                data-test="enrollment-last-year-num"
                                @click="enrollmentClicked"
                                @keydown.enter="enrollmentClicked"
                                @keydown.space.prevent="enrollmentClicked"
                            >
                                <v-icon class="changes-caret">{{ `fa-caret-${fromLastYear.delta}` }}</v-icon>
                                {{ fromLastYear.num }}
                            </div>
                            <div class="flex-grow-1 changes-category">
                                from this time last year
                            </div>
                        </div>
                        <div v-else class="d-flex changes-row">
                            <div
                                class="changes-value"
                                tabindex="0"
                                role="button"
                                aria-label="View enrollment details: no change from last year"
                                data-test="enrollment-last-year-num"
                                @click="enrollmentClicked"
                                @keydown.enter="enrollmentClicked"
                                @keydown.space.prevent="enrollmentClicked"
                            >
                                <div >{{ fromLastYear.num }}</div>
                            </div>
                            <div class="flex-grow-1 changes-category">
                                no change from last year
                            </div>
                        </div>

                        <div v-if="fromLastMonth.num != 0" class="d-flex changes-row">
                            <div
                                class="changes-value"
                                tabindex="0"
                                role="button"
                                :aria-label="`View enrollment change from last month: ${fromLastMonth.delta} ${fromLastMonth.num}`"
                                data-test="enrollment-last-month-num"
                                @click="enrollmentClicked"
                                @keydown.enter="enrollmentClicked"
                                @keydown.space.prevent="enrollmentClicked"
                            >
                                <v-icon class="changes-caret">{{ `fa-caret-${fromLastMonth.delta}` }}</v-icon>
                                {{ fromLastMonth.num }}
                            </div>
                            <div class="flex-grow-1 changes-category" style="width: 130px;">
                                from this time last month
                            </div>
                        </div>
                        <div v-else class="d-flex changes-row">
                            <div
                                class="changes-value"
                                tabindex="0"
                                role="button"
                                aria-label="View enrollment details: no change from last month"
                                data-test="enrollment-last-month-num"
                                @click="enrollmentClicked"
                                @keydown.enter="enrollmentClicked"
                                @keydown.space.prevent="enrollmentClicked"
                            >
                                <div>{{ fromLastMonth.num }}</div>
                            </div>
                            <div class="flex-grow-1 changes-category">
                            no change from last month
                            </div>
                        </div>
                    </div>
                </div>

                <highcharts
                    ref="enrollmentChart"
                    class="enr-chart"
                    :options="chartOptions"
                />
            </div>
        </template>
    </explore-widget>
</template>

<script>
import {mapState} from "vuex";
import ExploreWidget from "./ExploreWidget.vue";
import html2canvas from 'html2canvas';
import Printd from 'printd';

export default {
    name: 'EnrollmentCard',

    components: {
        ExploreWidget
    },

    data() {
        return {
            ready: false,
            params: null,
            config: null
        }
    },

    computed: {
        ...mapState('global', ['currentYear', 'defaultSchool', 'launchpadWidgetHeights']),

        totalEnrDisplay() {
            return new Intl.NumberFormat().format(this.params?.total_enrolled)
        },

        fromLastYear() {
            return this.display(this.params.from_this_time_last_year_enrolled)
        },

        fromLastMonth() {
            return this.display(this.params.from_last_month_enrolled)
        },

        schoolUrl() {
            return 'schools.php?action=get&school_year_id=' + this.currentYear.id
        },

        chartOptions() {
            let categories = this.params?.charts?.map(x => {
                let y = x.school_year
                return y.slice(0,5) + y.slice(7)
            })

            let me = this
            let series = [{
                name: 'Enrolled Students',
                data: this.params?.charts?.map(x => x.student_count),
                color: '#006c90',
                cursor: 'pointer',
                point: {
                    events: {
                        click: function (event) {
                            me.enrollmentClicked()
                        }
                    }
                }
            }]

            return {
                chart: {
                    events: {
                        load: function() {
                            const series = this.series[0]
                            series.group.element.setAttribute('data-test', 'enrollment-summary-line')
                        }
                    },
                    height: '170px'
                },
                credits: {
                    enabled: false
                },
                navigation: {
                    buttonOptions: {
                        enabled: false
                    }
                },
                title: {
                    text: '',
                },
                xAxis: {
                    categories,
                    title: {
                        text: undefined
                    },
                },
                yAxis: {
                    title: {
                        text: undefined
                    },
                    labels: {
                        formatter: x => new Intl.NumberFormat().format(x.value)
                    }
                },
                legend: {
                    enabled: false
                },
                plotOptions: {
                    line: {
                        marker: {
                            enabled: false
                        }
                    }
                },
                series
            }
        }
    },

    mounted() {
        this.loadEnrollment()
    },

    methods: {

        loadEnrollment() {
            let url = this.$models.getUrl('enrollmentSummary', 'read');

            return this.$axios.get(url)
                .then(response => {
                    if (response?.data.summary) {
                        this.params = response.data.summary
                        this.ready = true
                    } else {
                        this.ready = false
                    }
                })
        },

        display(value) {
            let delta = value < 0 ? 'down' : 'up'

            return {
                num: new Intl.NumberFormat().format(Math.abs(value)),
                delta,
            }
        },

        enrollmentClicked() {
            this.$dockableWindow({
                name: 'Enrollment',
                component: 'enrollment-reporting',
                attrs: {
                    params: {}
                }
            })
        },

        printChart() {
            html2canvas(document.querySelector('#enrollment-data-viz')).then(canvas => {
                const imgURL = canvas.toDataURL('image/jpeg')
                const image = document.createElement('img')
                image.src = imgURL
                image.setAttribute("id", 'print-content')
                document.body.appendChild(image)
                const printD = new Printd();
                printD.print(document.getElementById('print-content'))
                document.body.removeChild(image)
            })
        },

        exportChart() {
            let enrollmentDiv = document.querySelector('#enrollment-data-viz')
            enrollmentDiv.classList.add('download-padding')

            html2canvas(enrollmentDiv).then(canvas => {
                const link = document.createElement('a')
                link.setAttribute('download', 'Enrollment-Summary.png')
                link.setAttribute('href', canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream'))
                link.click()
            })
            setTimeout(() => {enrollmentDiv.classList.remove('download-padding')}, 20)
        }
    }
}
</script>

<style scoped lang="scss">

.enr-card-body {

    .enr-card-details {
        height: 70px;

        .enr-total {
            color: var(--v-primary-base);
            font-size: 24px;
            line-height: 31px;
            text-align: left;
        }

        .enr-total-num {
            font-weight: 600;

            &:focus {
                outline: 2px solid #2B87FF;
                outline-offset: 2px;
            }
        }

        .enr-total-text {
            text-align: left;
            font-size: 14px;
            font-weight: unset;
            color: #495b8f;
        }

        .enr-changes {
            width: 240px;
            color: var(--v-primary-base);

            .changes-row {
                min-height: 25px;

                .changes-value {
                    text-align: right;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 20px;
                    padding-right: 8px;
                    width: 86px;
                    cursor: pointer;

                    .changes-caret {
                        font-size: 20px;
                        color: inherit !important;
                    }

                    &:focus {
                        outline: 2px solid #2B87FF;
                        outline-offset: 2px;
                    }
                }

                .changes-category {
                    text-align: left;
                    font-size: 12px;
                    color: #495b8f;
                    line-height: 16px;
                }
            }
        }
    }

    .enr-chart {
        height: 180px;
        padding-top: 20px;
        ::v-deep * {
            font-family: 'HyperlegibleSans-Regular', Hyperlegible Sans, 'Roboto', sans-serif !important;
        }
    }
}

.download-padding {
    padding-left: 5px;
}

.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 350px !important;
}
</style>
